import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../Sidebar';
import {
    ArrowRight, PiggyBank, LayoutDashboard,
    RefreshCcw, Wallet, Coins, Zap, FileText, BarChart3, Banknote, FileWarning, Smartphone, Receipt
} from 'lucide-react';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';

import FinanceiroEstorno from './estorno/FinanceiroEstorno';
import FinanceiroPettyCash from './pettycash/FinanceiroPettyCash';
import FinanceiroTroco from './troco/FinanceiroTroco';
import FinanceiroAutomacoes from './automacao/FinanceiroAutomacoes';
import FinanceiroFita from './fita/FinanceiroFita';
import FinanceiroRelatorios from './relatorios/FinanceiroRelatorios';
import FinanceiroNumerario from './numerario/FinanceiroNumerario';
import Nulos from './nulos/Nulos';
import FinanceiroGprs from './gprs/FinanceiroGprs';
import Cofrinho from './cofrinho/Cofrinho';
import MapaCaixa from './mapa-caixa/MapaCaixa';
import { perfilNoModulo } from '../../utils/perfisDerivados';


// 1. DEFINA AS PERMISSÕES
// ⚠️ `Marketing` está em TODOS os cards abaixo (pedido da Julia, 30/09/2026): o
//    perfil tem ACESSO TOTAL ao Financeiro (bypass, como Admin — ver
//    `perfis-derivados.js` › ACESSO_TOTAL_POR_MODULO). Esta lista só decide se o
//    card aparece; a trava real é o `authorizeRoles` do backend, que já libera pelo
//    total. Diferente do `Operacional`, que é recortado por submódulo, o Marketing
//    entra em todos — por isso aparece em cada linha.
const PERMISSIONS = {
    'estorno': ['Financeiro', 'Diretoria', 'Admin','Gerente Adm', 'Gerente','Gerente Plantao', 'Auditoria', 'Coordenador de Qualidade', 'Operacional', 'Marketing'],
    // ⚠️ `Coordenador de Qualidade` entrou em 17/09/2026 (Julia), equiparado ao
    //    Gerente: registra despesa, vê o Controle da própria loja, exclui o
    //    próprio lançamento do mês vigente e confirma o saque de mês encerrado.
    //    Mudou aqui, muda no `authorizeRoles` do `server-pettycash.js` (`/sacar`
    //    e `DELETE /:id`) e nas travas do `FinanceiroPettyCashControle.js` —
    //    esta lista só decide se o card aparece.
    'petty-cash': ['Financeiro', 'Diretoria', 'Admin','Gerente Adm', 'Gerente', 'Gerente Plantao','Auditoria', 'Coordenador de Qualidade', 'Operacional', 'Marketing'],
    'troco': ['Financeiro', 'Diretoria', 'Admin', 'Gerente Adm', 'Gerente', 'Gerente Plantao', 'Auditoria', 'Operacional', 'Marketing'], // Gerente Plantão tem o MESMO acesso do Gerente
    'automacoes': ['Financeiro','Diretoria', 'Admin','Gerente Adm', 'Marketing'],
    // ⚠️ 'Operacional' entrou em Fita/Relatórios/Numerário/GPRS/Mapa de Caixa e 'Diretoria'
    //    no Cofrinho em 30/10/2026 (liberação de LEITURA do Financeiro inteiro para Diretoria
    //    e Operacional, pedido do Rodrigo). Esta lista só decide se o card aparece; a trava de
    //    LEITURA real (e a importação/escrita que segue restrita) mora no authorizeRoles do
    //    backend. O card "Automações" (só uploads/operação) NÃO recebe o Operacional — a parte
    //    visualizável dele é o "Relatórios".
    'fita': ['Financeiro', 'Diretoria', 'Admin', 'Gerente Adm', 'Marketing', 'Operacional'],
    'relatorios': ['Financeiro', 'Diretoria', 'Admin', 'Gerente Adm', 'Marketing', 'Operacional'],
    // Numerário é conferência de escritório: cruza venda × caixa × depósito da
    // rede inteira. Não é tela de loja — o gerente não tem o que fazer aqui.
    'numerario': ['Financeiro', 'Diretoria', 'Admin', 'Contabil', 'Marketing', 'Operacional'],
    // Nulos em Progresso — ⚠️ VEIO DO MÓDULO AUDITORIA em 2026-08-28, no mesmo dia
    // em que nasceu (hub, rota, tabela e pasta do SharePoint mudaram junto).
    // ⚠️ UMA chave para as DUAS telas: elas são abas internas do sub-módulo
    //    (`nulos/Nulos.js`), como no Estorno e no Petty Cash. Quem separa registro
    //    de consulta é o sub-módulo, não o hub.
    // ⚠️ `Auditoria` e `Financeiro` entram e veem só o Controle — conferem, não
    //    declaram (a trava real é o `authorizeRoles` do backend; aqui é a aba que
    //    não aparece).
    // ⚠️ `Gerente Adm` fica fora: é do escritório, não tem POS a declarar. É a
    //    única aba do módulo em que ele não entra, e é deliberado.
    // ⚠️ `Coordenador de Qualidade` entrou em 17/09/2026 (Julia) e REGISTRA: ele
    //    está na loja e opera o POS. Quem manda de verdade é o `PERFIS_REGISTRO`
    //    do `nulos-regras.js` — esta lista só decide se o card aparece.
    'nulos': ['Financeiro', 'Auditoria', 'Admin', 'Diretoria', 'Gerente', 'Gerente Plantao', 'Coordenador de Qualidade', 'Operacional', 'Marketing'],
    // Controle de GPRS — a planilha do TEF movel que saiu do SharePoint (16/09/2026).
    // ⚠️ `Diretoria` entra e SÓ LÊ (a aba de digitação nem aparece): ela acompanha
    //    a rede, não preenche o fechamento diário de uma loja. A trava real é o
    //    `authorizeRoles` do `server-gprs.js`.
    // ⚠️ Lista FECHADA pela Julia em 16/09/2026. `Gerente Adm` saiu (é do
    //    escritório, não tem maquininha); entraram `Gerente Plantao` (cobre o
    //    titular num controle diário) e `Coordenador de Qualidade`.
    // ⚠️⚠️ `Coordenador de Qualidade` é PERFIL RESTRITO, e só alcança esta aba
    //    porque o landing dele já é `/financeiro` (`src/utils/perfisRestritos.js`).
    //    Era a 2ª aba dele aqui (a 1ª é o Estorno).
    // ⚠️ Mudou aqui, muda em `PERFIS_LEITURA` do `gprs-regras.js` — a trava real é
    //    o `authorizeRoles` do backend; esta lista só decide se o card aparece.
    'gprs': ['Financeiro', 'Diretoria', 'Admin', 'Gerente', 'Gerente Plantao', 'Coordenador de Qualidade', 'Marketing', 'Operacional'],
    // Cofrinho — arrecadação guardada no cofre da loja (por banana) para repasse
    // ao Instituto Ronald (DEM-00035). A LOJA registra (Gerente, Gerente Plantao,
    // Coordenador de Qualidade) e o Financeiro/Admin consolidam.
    // ⚠️ `Financeiro` só CONSULTA (a aba de registro nem aparece — ver Cofrinho.js);
    //    a trava real é o `PERFIS_REGISTRO` do backend.
    // ⚠️ `Diretoria` e `Operacional` entraram em 30/10/2026 (liberação de LEITURA do
    //    Financeiro inteiro, pedido do Rodrigo), REVERTENDO o escopo fechado pela Julia
    //    em 2026-09-30 que os deixava de fora. Como o `Financeiro`, só CONSULTAM — não
    //    registram nem cancelam (ver PERFIS_LEITURA/REGISTRO/CANCELAMENTO em cofrinho-regras.js).
    'cofrinho': ['Financeiro', 'Admin', 'Gerente', 'Gerente Plantao', 'Coordenador de Qualidade', 'Marketing', 'Diretoria', 'Operacional'],
    // Mapa de Caixa — a digitalização do "Controle Diário" de papel (scan + totais)
    // por loja/dia (DEM-00036). ⚠️ `Diretoria` entra e SÓ LÊ (a aba de lançamento
    // nem aparece — ver MapaCaixa.js). Espelho de `PERFIS_LEITURA` do
    // `mapa-caixa-regras.js`; a trava real é o `authorizeRoles` do backend —
    // esta lista só decide se o card aparece.
    'mapa-caixa': ['Financeiro', 'Diretoria', 'Admin', 'Gerente', 'Gerente Plantao', 'Coordenador de Qualidade', 'Marketing', 'Operacional'],
};

// 2. DEFINA O ARRAY DE MÓDULOS
const modulesList = [
    {
        id: 'automacoes',
        label: 'Automações',
        icon: Zap,
        color: '#68b9af',
        bgColor: '#f0fdfa',
        desc: 'Automatize processos financeiros: conciliação Adyen, conferência PIX, faturas de cartão corporativo e fechamento mensal.',
        stat: 'Automações'
    },
    { 
        id: 'estorno', 
        label: 'Estorno de Venda', 
        icon: RefreshCcw, 
        color: '#0d7a74', 
        bgColor: '#ccfbf1', 
        desc: 'Registre e acompanhe pedidos de estorno financeiro para transações de cartão e pagamentos.', 
        stat: 'Devoluções' 
    },
    {
        id: 'fita',
        label: 'Fita',
        icon: FileText,
        color: '#0d7a74',
        bgColor: '#f0fdfa',
        desc: 'Consulta de pagamentos por meio de pagamento, filtrada por data e loja. Cancelados excluídos automaticamente.',
        stat: 'Consulta'
    },
    { 
        id: 'petty-cash', 
        label: 'Petty Cash', 
        icon: Wallet, 
        color: '#16a39c', 
        bgColor: '#e0f2f1', 
        desc: 'Gerencie o fundo fixo de caixa (caixinha) para pequenas despesas do dia a dia.', 
        stat: 'Fundo Fixo' 
    },
    {
        id: 'troco',
        label: 'Troco',
        icon: Coins,
        color: '#2dd4bf',
        bgColor: '#f0fdfa',
        desc: 'Controle as solicitações e a disponibilidade de troco para a operação diária.',
        stat: 'Suprimento'
    },
    {
        id: 'numerario',
        label: 'Numerário',
        icon: Banknote,
        color: '#0f766e',
        bgColor: '#f0fdfa',
        desc: 'Confere a venda em dinheiro (Sovos) com o que a loja declarou no caixa (Flex) e com o que a Prosegur depositou no banco.',
        stat: 'Conciliação'
    },
    {
        id: 'nulos',
        label: 'Nulos em Progresso',
        icon: FileWarning,
        color: '#0d7a74',
        bgColor: '#f0fdfa',
        desc: 'A loja declara o pedido cancelado no POS, com a foto da tela; o escritório acompanha os registros.',
        stat: 'Registro e Controle'
    },
    {
        id: 'gprs',
        label: 'Controle de GPRS',
        icon: Smartphone,
        color: '#16a39c',
        bgColor: '#f0fdfa',
        desc: 'A loja lança dia a dia a venda do TEF móvel (a maquininha do salão) e o portal calcula a diferença contra a Fita POS e o Flex.',
        stat: 'TEF Móvel'
    },
    {
        id: 'cofrinho',
        label: 'Cofrinho',
        icon: PiggyBank,
        color: '#16a39c',
        bgColor: '#e0f2f1',
        desc: 'A loja registra a arrecadação guardada no cofre (por banana); o Financeiro consolida por competência e repassa ao Instituto Ronald.',
        stat: 'Arrecadação'
    },
    {
        id: 'mapa-caixa',
        label: 'Mapa de Caixa',
        icon: Receipt,
        color: '#0d7a74',
        bgColor: '#f0fdfa',
        desc: 'A loja anexa o scan do Controle Diário e transcreve os totais do dia; o portal calcula a Quebra de Caixa e a Diferença GPRS.',
        stat: 'Fechamento'
    },
    {
        id: 'relatorios',
        label: 'Relatórios',
        icon: BarChart3,
        color: '#0d7a74',
        bgColor: '#ccfbf1',
        desc: 'Relatórios prontos para consulta na tela e exportação em Excel. Hoje: Fechamento Mensal (visão Financeira).',
        stat: 'Relatórios'
    }
];

const FinanceiroPainel = () => { 
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const userRole = perfilNoModulo(user?.perfil, 'financeiro') || '';
    
    // Deep-link via querystring (ex.: card de pendências da Home):
    //   ?tab=estorno&sub=controle&status=Aguardando Retorno da Loja
    //   → abre a aba Estorno, sub-aba Controle, já filtrada por esse status.
    const [searchParams] = useSearchParams();
    const tabInicial = searchParams.get('tab');
    const subInicial = searchParams.get('sub');
    const statusInicial = searchParams.get('status');

    const [isMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [activeTab, setActiveTab] = useState(tabInicial || 'dashboard');
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Variável movida para CIMA do OverviewWidgets
    // ⚠️ ORDEM ALFABÉTICA (pedido da Julia, 16/09/2026) — vale para os cards da
    //    Visão Geral E para os botões da barra de navegação, que leem esta mesma
    //    lista. Ordenar só num dos dois lugares deixaria o mesmo módulo em
    //    posições diferentes nas duas leituras da tela.
    // ⚠️ `localeCompare('pt-BR')` e nunca `a > b`: a comparação crua ordena por
    //    código de caractere, e aí "Automações" cairia depois de "Troco" (o 'ç' e
    //    as vogais acentuadas ficam fora da faixa A-Z).
    const allowedModules = modulesList
        .filter(mod => PERMISSIONS[mod.id]?.includes(userRole))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    
    // --- COMPONENTE EXTRAÍDO: WIDGETS ---
    const OverviewWidgets = () => (
        <div>
            {/* BANNER MODERNO */}
            <div style={{ background: 'linear-gradient(135deg, #2dd4bf 0%, #0d7a74 100%)', borderRadius: '16px', padding: '40px', color: '#fff', marginBottom: '32px', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 15px -3px rgba(79, 70, 229, 0.3)' }}>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>Financeiro</h2>
                    <p style={{ color: '#e0e7ff', fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>Gerencie e controle os processos financeiros das lojas com rastreabilidade, automação e consulta de dados operacionais.</p>
                </div>
                <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.15 }}>
                    <PiggyBank size={250} color="#ffffff" />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                {allowedModules.map((mod) => (
                    <div key={mod.id} onClick={() => setActiveTab(mod.id)} style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color }}></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', width: '100%' }}>
                            <div>
                                <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: mod.color, marginBottom: '8px', margin: 0 }}>{mod.stat}</p>
                                <h3 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{mod.label}</h3>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                                <mod.icon size={26} />
                            </div>
                        </div>
                        <div style={{ marginTop: 'auto' }}>
                            <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{mod.desc}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: mod.color }}>
                                <span>Acessar</span> <ArrowRight size={16} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    useEffect(() => {
        const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    useEffect(() => {
        if (activeTab !== 'dashboard' && !PERMISSIONS[activeTab]?.includes(userRole)) {
            setActiveTab('dashboard');
        }
    }, [activeTab, userRole]);

    const contentBoxStyle = { 
    backgroundColor: '#fff', 
    borderRadius: '16px', 
    border: '1px solid #e2e8f0', 
    padding: '32px', 
    minHeight: '75vh',
    width: '100%' 
    };

    // Verbete do Manual que abre expandido conforme a aba ativa.
    // (O botão "Manual da Tela" mostra TODO o módulo Financeiro; isto só escolhe o ponto de partida.)
    // Antes esta função devolvia JSX por engano — o HelpCenter espera um id (string).
    const getSecaoAjuda = () => {
        switch (activeTab) {
            case 'estorno':    return 'estorno';
            case 'petty-cash': return 'petty-cash';
            case 'troco':      return 'troco';
            case 'fita':       return 'fita';
            case 'automacoes': return 'conciliacao-adyen';
            case 'nulos':      return 'nulos';
            case 'gprs':       return 'gprs';
            case 'relatorios': return 'fechamento-financeiro';
            default:           return 'visao-geral-financeiro';
        }
    };

    const getMarginLeft = () => { return window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'); };

    // Ponto e vírgula removido do IF
    const renderContent = () => {
        if (activeTab === 'dashboard') return <OverviewWidgets />;
        switch(activeTab) {
            case 'estorno': return <div style={contentBoxStyle}><FinanceiroEstorno subInicial={subInicial} statusInicial={statusInicial} /></div>;
            case 'petty-cash': return <div style={contentBoxStyle}><FinanceiroPettyCash /></div>;
            case 'troco': return <div style={contentBoxStyle}><FinanceiroTroco /></div>;
            case 'automacoes': return <div style={contentBoxStyle}><FinanceiroAutomacoes /></div>;
            case 'fita': return <div style={contentBoxStyle}><FinanceiroFita /></div>;
            case 'numerario': return <div style={contentBoxStyle}><FinanceiroNumerario /></div>;
            // Sub-módulo com abas internas (Registrar · Controle), como o Estorno.
            case 'nulos': return <div style={contentBoxStyle}><Nulos /></div>;
            // Sub-módulo com abas internas (Grade do mês · Consulta).
            case 'gprs': return <div style={contentBoxStyle}><FinanceiroGprs /></div>;
            // Sub-módulo com abas internas (Registrar · Consulta).
            case 'cofrinho': return <div style={contentBoxStyle}><Cofrinho /></div>;
            // Sub-módulo com abas internas (Lançar · Consulta).
            case 'mapa-caixa': return <div style={contentBoxStyle}><MapaCaixa /></div>;
            case 'relatorios': return <div style={contentBoxStyle}><FinanceiroRelatorios /></div>;
            default: return null;
        }
    };

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>
                
                {/* CABEÇALHO — padrão dos módulos, na cor do Financeiro (verde-petróleo) */}
                <ModuloHeader
                    eyebrow="Módulo"
                    titulo="Financeiro"
                    cor="#0d7a74"
                    onAbrirManual={() => setIsHelpOpen(true)}
                    onAbrirMenu={() => setIsCollapsed(false)}
                    isMobile={isMobile}
                />

                <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                    
                    {/* BARRA DE NAVEGAÇÃO */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px', backgroundColor: '#fff', padding: '8px', borderRadius: '12px', border: '1px solid #e2e8f0', width: '100%' }}>
                        
                        <button onClick={() => setActiveTab('dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', background: activeTab === 'dashboard' ? 'linear-gradient(135deg, #16a39c 0%, #0d7a74 100%)' : 'transparent', color: activeTab === 'dashboard' ? '#fff' : '#64748b', boxShadow: activeTab === 'dashboard' ? '0 4px 6px rgba(59, 130, 246, 0.3)' : 'none', whiteSpace: 'nowrap' }}>
                            <LayoutDashboard size={18} /> Visão Geral
                        </button>
                        
                        <div style={{ minWidth: '1px', backgroundColor: '#e2e8f0', margin: '0 5px' }}></div>
                        
                        {allowedModules.map((mod) => (
                            <button key={mod.id} onClick={() => setActiveTab(mod.id)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', border: activeTab === mod.id ? `2px solid ${mod.color}` : '2px solid transparent', backgroundColor: activeTab === mod.id ? mod.bgColor : 'transparent', color: activeTab === mod.id ? mod.color : '#64748b', whiteSpace: 'nowrap' }}>
                                <mod.icon size={18} /> {mod.label}
                            </button>
                        ))}
                    </div>
                    
                    {/* Linha descomentada para exibir o conteúdo */}
                    {renderContent()}
                </div>
            </div>

            <HelpCenter 
                isOpen={isHelpOpen} 
                onClose={() => setIsHelpOpen(false)} 
                secaoInicial={getSecaoAjuda()}
                modulo="financeiro"
            />
        </div>
    );
};

export default FinanceiroPainel;